import React from 'react';
import { usePathname } from 'next/navigation';
import { useI18n } from 'next-localization';

import { useFormatedSiteTitle } from '@/hooks/use-formatted-site-title';
import {
  BOOKING_PAGE,
  CONFIRMATION_PAGE,
  INITIAL_HOST_TITLE,
  LISTING_PAGE,
  PROFILE_PAGE,
} from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useOasApp } from '@/providers/oas-app-provider';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { TitleBanner } from '@xsf/shared-lib/components/title-banner';

import styles from '../assets/common.module.css';

const LISTING_PAGE_SITECORE_TEMPLATE_NAME = 'OAS Listing';
const PROFILE_PAGE_SITECORE_TEMPLATE_NAME = 'OAS Profile';
const BOOKING_PAGE_SITECORE_TEMPLATE_NAME = 'OAS Booking';
const CONFIRMATION_PAGE_SITECORE_TEMPLATE_NAME = 'OAS Confirmation';

interface OASPageTitleBannerProps {
  rendering?: { componentName: string };
  className?: string;
  fallbackTitle?: Field<string>;
}

const OASPageTitleBanner: React.FC<OASPageTitleBannerProps> = ({
  rendering,
  className,
  fallbackTitle,
}) => {
  const { sitecoreContext } = useSitecoreContext();
  const routeExists = sitecoreContext?.route !== undefined;
  const pathname = usePathname();
  const { t } = useI18n();
  const { breadcrumbs, pageTitle, pageShortDescription, isMho } = useOasApp();

  const initialHostTitleValue =
    typeof window !== 'undefined' ? sessionStorage.getItem(INITIAL_HOST_TITLE) : '';
  const initialHostTitle = initialHostTitleValue
    ? `${initialHostTitleValue} ${t('OAS.LandingPage.Title')}`
    : '';

  let formattedSiteTitle = useFormatedSiteTitle({
    fallbackTitle,
    returnType: 'field',
  }) as Field<string>;

  if (initialHostTitle) {
    formattedSiteTitle = { value: initialHostTitle } as Field<string>;
  }

  const title = routeExists
    ? pageTitle || (sitecoreContext.route?.fields?.pageTitle as Field<string>)
    : fallbackTitle;

  // Note: added a check for template name or id in addition to pathname check

  const isConfirmationPage =
    sitecoreContext?.route?.templateName === CONFIRMATION_PAGE_SITECORE_TEMPLATE_NAME ||
    pathname?.toLowerCase().includes(CONFIRMATION_PAGE);

  const isBookingPage =
    sitecoreContext?.route?.templateName === BOOKING_PAGE_SITECORE_TEMPLATE_NAME ||
    pathname?.toLowerCase().includes(BOOKING_PAGE);

  const isListing =
    sitecoreContext?.route?.templateName === LISTING_PAGE_SITECORE_TEMPLATE_NAME ||
    pathname?.toLowerCase().includes(LISTING_PAGE);

  const isProfile =
    sitecoreContext?.route?.templateName === PROFILE_PAGE_SITECORE_TEMPLATE_NAME ||
    pathname?.toLowerCase().includes(PROFILE_PAGE);

  const showDescription =
    !isConfirmationPage && !isBookingPage && !isProfile && pageShortDescription;
  const withBreadcrumbs = !isMho;

  return (
    // The id to help target element and calculate in a precise way the offset for scrolling
    <div id="oas-page-title-banner">
      <TitleBanner
        title={isListing ? formattedSiteTitle : title}
        description={showDescription ? pageShortDescription : undefined}
        isLarge={false}
        dataTestId={rendering?.componentName as string}
        className={cn(
          {
            [styles.hideSubHeaderOnMobile]: isProfile,
          },
          className
        )}
        withBreadcrumbs={withBreadcrumbs}
        breadcrumbs={breadcrumbs}
      />
    </div>
  );
};

export default OASPageTitleBanner;
